import { useMemo } from 'react';
import {
  ChakraFlex,
  UniversalSelect,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  FAIconName,
} from '@equip.health/ui';
import { sortBy } from 'lodash';

import { TaskStatusLabel } from '~/lib/util/task.util';
import { useGetTaskVoidReasonsEnumQuery } from '~/store/services/enumerations';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';

interface VoidTaskModalV2Props {
  id: string;
  isOpen: boolean;
  voidReason: string;
  setVoidReason: (voidReason: string) => void;
  closeModal: () => void;
  currentTask: any;
  onChangeStatus: (taskId: string, status: string, voidReason: string) => void;
  isLoading?: boolean;
}

const VoidTaskModalV2 = ({
  id,
  isOpen,
  voidReason,
  setVoidReason,
  closeModal,
  currentTask,
  onChangeStatus,
  isLoading,
}: VoidTaskModalV2Props) => {
  const { data: listOfVoidReasons } = useGetTaskVoidReasonsEnumQuery();

  const voidReasonOptions = useMemo(
    () =>
      sortBy(
        (listOfVoidReasons ?? [])
          .filter((reason) => reason.statusCode === TaskStatusLabel.VOID)
          .map(({ externalVoidReasonId, voidReason }) => ({
            label: voidReason,
            value: externalVoidReasonId,
          })),
        ['label'],
      ),
    [listOfVoidReasons],
  );

  const handleClose = (): void => {
    closeModal();
  };

  const handleConfirm = () => {
    onChangeStatus(currentTask.id, currentTask.status, voidReason);
    closeModal();
  };

  return (
    <ModalV2
      footerContent={
        <ChakraFlex justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${id}__void-task-cancel`}
            leftIcon={FAIconName.XmarkRegular}
            onClick={handleClose}
            variant="secondary"
          >
            Cancel
          </FireflyButton>

          <FireflyButton
            id={`${id}__void-task-confirm`}
            isDisabled={!voidReason}
            isLoading={isLoading}
            onClick={handleConfirm}
            variant="accent.critical"
          >
            Void task
          </FireflyButton>
        </ChakraFlex>
      }
      id={`${id}__void-task-modal-v2`}
      isOpen={isOpen}
      onClose={handleClose}
      title="Void task"
    >
      <ChakraFlex flexDirection="column" gap="16px">
        <Text color="neutral.primary" variant="body">
          Are you sure you want to void this task? It's impossible to recover
          this task.
        </Text>

        <InputGroupV2 id={`${id}__void-task-reason-group`} label="Reason">
          <UniversalSelect
            id={`${id}__void-task-reason-select`}
            isSingleSelect
            modalId={`${id}__void-task-reason-select-modal`}
            onChange={(option) => {
              setVoidReason(option?.value ?? '');
            }}
            options={voidReasonOptions}
            placeholder="Select one"
            value={getSelectedUniversalSelectOption(
              voidReason,
              voidReasonOptions,
            )}
          />
        </InputGroupV2>
      </ChakraFlex>
    </ModalV2>
  );
};

export default VoidTaskModalV2;
